import React, { useState, useEffect } from 'react';

//Libs
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

// Components
import ModalCard from '../../../msreact-kit/html-components/dialog-box/modal-card';
import SnackBar from '../../../components/Shared/Utils/SnackBar';
import Loader from '../../../components/Shared/Utils/Loader';
import { SupplierUserDetailsGrid } from './supplier-user-details-grid';

// Assets
import { CDN_LINK } from '../../../assets/img';

// Utils
import { handleGetFields } from '../../../utils/fields';
import {
  getCountryOptions,
  getAttribute
} from '../../../components/dashboard/UnifiedSellerDashboard/utils';
import { groupCountriesByISDCode } from '../supplier-management/utils';
import isEmpty from '../../../validator/isEmpty';

// Data
import {
  navigationButtons,
  initialSupplierAttributePayload,
  initialUserDetails,
  taxIdValidationList,
  contactValidationList
} from './data';

// Constants
import idConstants, { REGION } from '../../../data/idConstants';

// Actions
import {
  getRegionSpecificCountryList,
  getCountryList
} from '../../../actions/CountryAction';
import { updateSupplierAttributes } from '../../../actions/user-service-action';
import {
  getAllSupplierDetailsWithPagination,
  validateTaxIdentifier,
  createSupplier,
  updateSupplier
} from '../../../actions/backoffice-service-action';
import { setSnackbar } from '../../../actions/generic-action';

// CSS
import './index.scss';

const SupplierDetails = (props) => {
  const queryParams = new URLSearchParams(props.location?.search);
  const dispatch = useDispatch();
  const {
    supplierDetailsList,
    regionSpecificCountryList,
    countryList,
    supplierAttributes,
    isLoading,
    content,
    snackbarStatus
  } = useSelector((state) => {
    return {
      supplierDetailsList: state.UserServiceReducer.supplierDetailsList,
      regionSpecificCountryList: state.CountryReducer.regionSpecificCountryList,
      countryList: state.CountryReducer.countryList,
      supplierAttributes: state.UserServiceReducer.supplierAttributes,
      isLoading: state.OrderSummaryReducer.isLoading,
      content: state.ContentReducer.content,
      snackbarStatus: state.GenericReducer.snackbarStatus
    };
  });

  const supplierDetailsContent =
    content?.components?.user_management?.supplier_management?.supplier_details;
  const mode = queryParams.get('mode');
  const userDetailKeys = [
    'firstName',
    'lastName',
    'emailId',
    'isdCode',
    'contactNo'
  ];
  const taxIdentifierType =
    process.env.REACT_APP_REGION === REGION.IN.CODE
      ? idConstants.taxId.GST
      : idConstants.taxId.VAT;

  const [taxIdentifier, setTaxIdentifier] = useState();
  const [supplierAttributePayload, setSupplierAttributePayload] = useState(
    _.cloneDeep(initialSupplierAttributePayload)
  );
  const [supplierGridData, setSupplierGridData] = useState([]);
  const [supplierUserDetails, setSupplierUserDetails] = useState(
    _.cloneDeep(initialUserDetails)
  );
  const [showModal, setShowModal] = useState(false);
  const [allCountryOptions, setAllCountryOptions] = useState();
  const [regionCountryOptions, setRegionCountryOptions] = useState();

  useEffect(() => {
    dispatch(getRegionSpecificCountryList());
    dispatch(getCountryList(idConstants.typeOfUsage.contactNumber));
    dispatch(getAllSupplierDetailsWithPagination(props.location.state.supplierName));

    return () => {
      clearAttributes(supplierAttributes);
    };
  }, [props.location.state.supplierName]);

  useEffect(() => {
    if (!isEmpty(content)) {
      const supplierDetailsContent =
        content?.components?.user_management?.supplier_management?.supplier_details;
      modifyAttribute(
        supplierAttributes,
        'addSupplier',
        'displayName',
        `+ ${supplierDetailsContent?.add?.title}`
      );
      modifyAttribute(
        supplierAttributes,
        'taxIdentifier',
        'displayName',
        content?.constants?.taxId
      );
    }
  }, [content]);

  useEffect(() => {
    if (!regionCountryOptions || regionCountryOptions?.length <= 0) return;

    const selectedSupplierDetails = supplierDetailsList?.content?.find(
      (item) => item.supplierId === props.location.state.supplierId
    );
    if (selectedSupplierDetails) {
      const supplierDetails = {
        ...selectedSupplierDetails,
        userDetails: selectedSupplierDetails.userDetails?.map((user) => {
          const { roleName, countryCode, ...finalUserDetails } = user;
          return {
            ...finalUserDetails,
            emailVerified: user.emailVerified.toString()
          };
        })
      };

      setSupplierAttributePayload(supplierDetails);
      preFillValues(selectedSupplierDetails, regionCountryOptions);
    } else {
      setSupplierAttributePayload({
        ...supplierAttributePayload,
        countryCode: regionCountryOptions[0]?.id
      });
    }
  }, [supplierDetailsList, regionCountryOptions]);

  useEffect(() => {
    if (!regionSpecificCountryList?.length || !countryList?.length) return;

    const regionCountryOptions = getCountryOptions(
      regionSpecificCountryList,
      'countryName'
    );

    const groupedCountryData = groupCountriesByISDCode(countryList);
    const allCountryOptions = getCountryOptions(groupedCountryData, 'isdCode');

    modifyAttribute(
      supplierAttributes,
      'countryCode',
      'options',
      regionCountryOptions
    );
    modifyAttribute(supplierAttributes, 'isdCode', 'options', allCountryOptions);

    const isViewMode = mode === idConstants.supplierDetails.mode.view;
    const isAddMode = mode === idConstants.supplierDetails.mode.add;

    modifyAttribute(supplierAttributes, 'userDetails', 'hidden', isViewMode);
    modifyAttribute(navigationButtons, 'submit', 'hidden', isViewMode);

    if (isAddMode) {
      modifyAttribute(
        supplierAttributes,
        'countryCode',
        'value',
        regionCountryOptions[0]
      );
    }

    const taxIdentifierValidation =
      taxIdValidationList[regionCountryOptions[0]?.id]['taxIdentifier'];
    updateValidation('taxIdentifier', taxIdentifierValidation);

    if (regionCountryOptions.length === 1) {
      const isdCodeOption = allCountryOptions?.find(
        (item) => item.id === regionCountryOptions[0].id
      );
      modifyAttribute(supplierAttributes, 'isdCode', 'value', isdCodeOption);
      modifyAttribute(supplierAttributes, 'countryCode', 'disabled', true);
    } else if (isAddMode) {
      modifyAttribute(supplierAttributes, 'isdCode', 'value', allCountryOptions[0]);
    }

    dispatch(updateSupplierAttributes(supplierAttributes));

    setAllCountryOptions(allCountryOptions);
    setRegionCountryOptions(regionCountryOptions);
  }, [supplierAttributes, regionSpecificCountryList, countryList, mode]);

  useEffect(() => {
    const companyDetails = supplierAttributes?.find(
      (item) => item.name === 'companyDetails'
    )?.attributes;

    const userDetails = supplierAttributes
      ?.find((item) => item.name === 'userDetails')
      ?.attributes?.filter(
        (attr) =>
          attr.attributeType !== 'HIDDEN' && attr.attributeType !== 'PRIMARY_BUTTON'
      );

    const validatePhoneNumber = userDetails
      ?.filter((item) => {
        return item.attributeType === 'COMBINE';
      })
      .some((item) => {
        const contactNoField = item.attributes?.find(
          (attr) => attr.name === 'contactNo'
        );
        return contactNoField?.value?.length >= 5;
      });

    if (
      checkIfHasValue(companyDetails) &&
      checkIfHasValue(userDetails) &&
      validatePhoneNumber
    ) {
      modifyAttribute(supplierAttributes, 'addSupplier', 'disabled', false);
    } else {
      modifyAttribute(supplierAttributes, 'addSupplier', 'disabled', true);
    }
  }, [supplierAttributePayload, supplierAttributes, supplierUserDetails]);

  const preFillValues = (supplierDetails, countryOptions) => {
    Object.entries(supplierDetails).forEach((item) => {
      const name = item[0];
      const value = item[1];

      if (name === 'userDetails') {
        const tempUserDetails = [...value].map((item) => {
          const isdCode = item.contactNo?.split(' ')[0] ?? '';
          const contactNo = item.contactNo?.split(' ')[1] ?? '';
          return {
            supplierUserMappingId: item.supplierUserMappingId,
            firstName: item.firstName,
            lastName: item.lastName,
            emailId: item.emailId,
            emailVerified: String(item.emailVerified),
            isdCode: isdCode,
            contactNo: contactNo
          };
        });

        updateSupplierGrid(tempUserDetails);
      } else if (name === 'countryCode') {
        const countryCodeOption = countryOptions?.find((item) => item.id === value);
        const isdCodeOption = allCountryOptions?.find((item) => item.id === value);
        modifyAttribute(supplierAttributes, name, 'value', countryCodeOption);
        modifyAttribute(supplierAttributes, 'isdCode', 'value', isdCodeOption);
        modifyAttribute(supplierAttributes, name, 'disabled', !!value);
      } else {
        if (name === 'taxIdentifier') {
          setTaxIdentifier(value);
          modifyAttribute(supplierAttributes, name, 'disabled', !!value);
        }
        modifyAttribute(supplierAttributes, name, 'value', value);
      }
    });
  };

  const clearAttributes = (supplierAttributes) => {
    Object.entries(initialSupplierAttributePayload)?.forEach((item) => {
      const name = item[0];

      if (name === 'userDetails') {
        Object.entries(initialUserDetails)?.forEach((item) => {
          modifyAttribute(supplierAttributes, item[0], 'value', '');
          modifyAttribute(supplierAttributes, item[0], 'error', '');
          item[0] !== 'addSupplier' &&
            modifyAttribute(supplierAttributes, item[0], 'disabled', false);
        });
      } else {
        modifyAttribute(supplierAttributes, name, 'value', '');
        modifyAttribute(supplierAttributes, name, 'error', '');
        item[0] !== 'supplierName' &&
          modifyAttribute(supplierAttributes, name, 'disabled', false);
      }
    });
    modifyAttribute(navigationButtons, 'submit', 'disabled', true);
    dispatch(
      setSnackbar({
        showSnackbar: false,
        message: ''
      })
    );
  };

  const checkIfHasValue = (attributes) => {
    return attributes?.every((attr) => {
      if (attr.attributeType === 'COMBINE') return checkIfHasValue(attr.attributes);
      else return !!attr.value && !attr.error;
    });
  };

  const handleInputChange = (event) => {
    const { name, error, value } = event;

    let updatedValue = value;

    switch (name) {
      case 'taxIdentifier':
        updatedValue = updatedValue.toUpperCase().trim();
        break;

      case 'countryCode': {
        setSupplierAttributePayload({
          ...supplierAttributePayload,
          [name]: updatedValue?.id
        });
        const isdCodeOption = allCountryOptions?.find(
          (item) => item.id === updatedValue?.id
        );
        modifyAttribute(supplierAttributes, 'isdCode', 'value', isdCodeOption);
        if (updatedValue?.id && mode === idConstants.supplierDetails.mode.add) {
          const taxIdValidation =
            taxIdValidationList[updatedValue.id]['taxIdentifier'];
          modifyAttribute(supplierAttributes, 'taxIdentifier', 'value', '');
          modifyAttribute(supplierAttributes, 'taxIdentifier', 'error', '');
          updateValidation('taxIdentifier', taxIdValidation);
        }
        break;
      }

      case 'isdCode': {
        const contactValidation = contactValidationList[updatedValue?.id]
          ? contactValidationList[updatedValue?.id]['contactNo']
          : contactValidationList['default']['contactNo'];
        modifyAttribute(supplierAttributes, 'contactNo', 'value', '');
        modifyAttribute(supplierAttributes, 'contactNo', 'error', '');
        updateValidation('contactNo', contactValidation);
        break;
      }

      case 'addSupplier':
        handleClick();
        return;

      default:
        break;
    }

    modifyAttribute(supplierAttributes, name, 'value', updatedValue);
    modifyAttribute(supplierAttributes, name, 'error', error);

    if (!userDetailKeys.includes(name) && name !== 'countryCode') {
      setSupplierAttributePayload({
        ...supplierAttributePayload,
        [name]: value
      });
    }
    dispatch(updateSupplierAttributes(supplierAttributes));
  };

  const updateValidation = (name, validation) => {
    const validationRule = {
      rule: {
        required: {
          isRequired: true
        },
        regex: {
          pattern: validation.pattern
        },
        length: {
          min: 1,
          max: validation.charLimit
        }
      }
    };
    modifyAttribute(supplierAttributes, name, 'validation', validationRule);
  };

  const handleEditPage = (supplierId, supplierName) => {
    props.history.push({
      pathname: '/dashboard/user/supplier',
      state: {
        supplierId: supplierId,
        supplierName: supplierName
      },
      search: `?mode=${idConstants.supplierDetails.mode.edit}`
    });
  };

  const handleBlur = (event) => {
    const { name, value, error } = event;

    const countryDetails = getAttribute(supplierAttributes, 'countryCode', 'value');
    const countryCode = countryList?.find(
      (country) => country.countryName === countryDetails.label
    )?.countryCode;

    if (name === 'taxIdentifier' && value !== taxIdentifier && !error) {
      setTaxIdentifier(value);
      dispatch(validateTaxIdentifier(value, taxIdentifierType, countryCode)).then(
        (response) => {
          if (response) {
            modifyAttribute(
              supplierAttributes,
              'supplierName',
              'value',
              response.companyName
            );
            setSupplierAttributePayload({
              ...supplierAttributePayload,
              supplierName: response.companyName
            });
          }
        }
      );

      dispatch(updateSupplierAttributes(supplierAttributes));
    }
  };

  const updateSupplierGrid = (supplierGridData) => {
    const supplierUserDetails = supplierGridData?.map((item) => ({
      supplierUserMappingId: item.supplierUserMappingId,
      supplierName: props.location.state.supplierName,
      firstName: item.firstName,
      lastName: item.lastName,
      emailId: item.emailId,
      emailVerified: item.emailVerified,
      contactNo: item.isdCode + ' ' + item.contactNo
    }));
    setSupplierGridData(supplierUserDetails);
  };

  const handleClick = (event) => {
    const { name } = event.currentTarget;
    if (name === 'back') {
      props.history.push('/dashboard/user');
    } else if (name === 'submit') {
      setShowModal(true);
    } else if (name === 'addSupplier') {
      const userDetailsPayload = [...supplierAttributePayload.userDetails];

      const supplierUserMappingId = getAttribute(
        supplierAttributes,
        'supplierUserMappingId',
        'value'
      );

      if (userDetailsPayload?.length >= 10 && !supplierUserMappingId) {
        dispatch({
          type: 'ERROR',
          error: {
            data: {
              message: supplierDetailsContent.add.error.max_supplier_count
            }
          }
        });
        return;
      }

      const firstName = getAttribute(supplierAttributes, 'firstName', 'value');
      const lastName = getAttribute(supplierAttributes, 'lastName', 'value');
      const emailId = getAttribute(supplierAttributes, 'emailId', 'value');
      const isdCode = getAttribute(supplierAttributes, 'isdCode', 'value');
      const contactNo = getAttribute(supplierAttributes, 'contactNo', 'value');

      const isdCodeOption = allCountryOptions?.find(
        (item) => item.id === supplierAttributePayload.countryCode
      );

      const modifiedSupplierDetail = {
        supplierUserMappingId: supplierUserMappingId,
        firstName: firstName,
        lastName: lastName,
        emailId: emailId,
        contactNo: isdCode.label + ' ' + contactNo
      };

      const index = userDetailsPayload.findIndex(
        (item) => item.supplierUserMappingId === supplierUserMappingId
      );

      if (index !== -1) {
        userDetailsPayload[index] = {
          ...userDetailsPayload[index],
          ...modifiedSupplierDetail
        };
      } else {
        userDetailsPayload.push(modifiedSupplierDetail);
      }

      const payload = {
        ...supplierAttributePayload,
        taxIdentifierType: taxIdentifierType,
        userDetails: userDetailsPayload
      };

      const isUpdate = !!supplierAttributePayload?.supplierId;

      const action = isUpdate ? updateSupplier(payload) : createSupplier(payload);
      dispatch(action).then((response) => {
        if (response) {
          props.history.push({
            pathname: '/dashboard/user/supplier',
            state: {
              supplierId: response.supplierId,
              supplierName: response.supplierName
            },
            search: `?mode=${mode}`
          });
          dispatch(getAllSupplierDetailsWithPagination(response.supplierName));
          dispatch(
            setSnackbar({
              showSnackbar: true,
              message: supplierDetailsContent?.popup?.title,
              severity: 'success'
            })
          );
          modifyAttribute(supplierAttributes, 'supplierUserMappingId', 'value', '');
          modifyAttribute(supplierAttributes, 'firstName', 'value', '');
          modifyAttribute(supplierAttributes, 'lastName', 'value', '');
          modifyAttribute(supplierAttributes, 'emailId', 'value', '');
          modifyAttribute(supplierAttributes, 'isdCode', 'value', isdCodeOption);
          modifyAttribute(supplierAttributes, 'contactNo', 'value', '');

          modifyAttribute(supplierAttributes, 'countryCode', 'disabled', true);
          modifyAttribute(supplierAttributes, 'taxIdentifier', 'disabled', true);

          modifyAttribute(supplierAttributes, 'addSupplier', 'disabled', true);
          modifyAttribute(
            supplierAttributes,
            'addSupplier',
            'displayName',
            `+ ${supplierDetailsContent?.add?.title}`
          );
          modifyAttribute(supplierAttributes, 'emailId', 'disabled', false);

          modifyAttribute(navigationButtons, 'submit', 'disabled', false);
        }
      });
    }
  };

  const handleConfirm = () => {
    setShowModal(false);
    props.history.push('/dashboard/user');
  };

  const modifyAttribute = (attributes, name, key, value) => {
    attributes?.forEach((attr) => {
      if (attr.attributeType === 'COMBINE')
        modifyAttribute(attr.attributes, name, key, value);

      if (attr.name === name) {
        attr[key] = value;

        if (key === 'value') {
          if (userDetailKeys.includes(name)) {
            setSupplierUserDetails({
              ...supplierUserDetails,
              [name]: value
            });
          }
        }
      }
    });
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className='supplier-user-details__container'>
        <div className='supplier-user-details__container__item__title-block'>
          <h1 className='supplier-user-details__container__item__heading'>
            {supplierDetailsContent?.[mode]?.title}
          </h1>
          {mode === idConstants.supplierDetails.mode.view && (
            <div className='supplier-user-details__container__item__heading__action'>
              <img
                src={`${CDN_LINK}/${supplierDetailsContent?.edit?.icon}`}
                alt={supplierDetailsContent?.edit?.alt}
                title={supplierDetailsContent?.edit?.alt}
                onClick={() =>
                  handleEditPage(
                    props.location?.state?.supplierId,
                    props.location?.state?.supplierName
                  )
                }
                tabIndex={0}
                role='button'
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ')
                    handleEditPage(
                      props.location?.state?.supplierId,
                      props.location?.state?.supplierName
                    );
                }}
              />
            </div>
          )}
        </div>
        <div className='supplier-user-details__container__content'>
          {supplierAttributes?.map((supplierAttribute) =>
            handleGetFields(
              supplierAttribute,
              handleInputChange,
              handleBlur,
              handleClick
            )
          )}
          <div className='supplier-user-details__container__content__grid'>
            <SupplierUserDetailsGrid data={supplierGridData} mode={mode} />
          </div>
          <div className='supplier-user-details__container__content__navigation-buttons'>
            {navigationButtons?.map((item) =>
              handleGetFields(item, handleInputChange, handleBlur, handleClick)
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <div className='dialog_modal'>
          <div className={`dialog_modal__content`}>
            <ModalCard
              title={supplierDetailsContent?.popup?.title}
              subTitle={supplierDetailsContent?.popup?.description}
              primaryLabel={content?.constants?.okay}
              handleConfirm={handleConfirm}
              isSecondaryBtn={false}
              isSuccess={true}
            />
          </div>
        </div>
      )}
      {snackbarStatus?.showSnackbar && (
        <SnackBar
          severity={snackbarStatus?.severity}
          changeState={() =>
            dispatch(
              setSnackbar({
                showSnackbar: false,
                message: ''
              })
            )
          }
          message={snackbarStatus?.message}
        />
      )}
    </>
  );
};

export default SupplierDetails;
